import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Search, Compass, ShieldCheck, Award, HeartHandshake, 
  ArrowRight, Sparkles, Calendar, Users, Percent, MessageCircle, 
  ChevronDown, ChevronUp, Star, MapPin, BookOpen, Clock, CalendarDays 
} from 'lucide-react';
import { toursData } from '../data/tours';
import TourCard from '../components/TourCard';
import './Home.css';

export default function Home() {
  const navigate = useNavigate();
  
  // Hero slider slides
  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1593693397690-362cb9666fc2?auto=format&fit=crop&w=1600&q=80',
      title: 'Enchanting Kerala Backwaters',
      subtitle: 'Experience tranquil houseboats, tea-gardens in Munnar, and golden sands in Kovalam.',
      badge: 'Domestic Best Seller',
      link: '/tour/kerala-experience'
    },
    {
      image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1600&q=80',
      title: 'Vibrant Tropics of Bali',
      subtitle: 'Immerse yourself in rich Hindu culture, emerald rice terraces, and gorgeous sunsets.',
      badge: 'Tropical Paradise',
      link: '/tour/bali-culture-beach'
    },
    {
      image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=1600&q=80',
      title: 'Scenic Swiss Alps Adventure',
      subtitle: 'Bask in snow-capped mountains, alpine villages, and pure swiss luxury.',
      badge: 'European Escape',
      link: '/tour/swiss-alps-paradise'
    },
    {
      image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=1600&q=80',
      title: 'Leh Ladakh Mountain Odyssey',
      subtitle: 'Traverse high mountain passes and camp next to the sapphire waters of Pangong Lake.',
      badge: 'Himalayan Adventure',
      link: '/tour/leh-ladakh-adventure'
    }
  ];

  const [activeSlide, setActiveSlide] = useState(0);

  // Auto rotate slides
  useEffect(() => {
    const slideTimer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(slideTimer);
  }, [slides.length]);

  // Smart search widget state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState('all');
  const [searchDate, setSearchDate] = useState('');
  const [searchGuests, setSearchGuests] = useState(1);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const queryParams = [];
    if (searchQuery) queryParams.push(`search=${encodeURIComponent(searchQuery)}`);
    if (searchDate) queryParams.push(`date=${searchDate}`);
    if (searchGuests > 1) queryParams.push(`guests=${searchGuests}`);

    const paramStr = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    if (searchCategory === 'domestic') {
      navigate(`/domestic${paramStr}`);
    } else if (searchCategory === 'international') {
      navigate(`/international${paramStr}`);
    } else {
      // Find matches in categories to redirect intelligently
      const hasInternationalMatch = toursData.some(
        (t) => t.category === 'International' && t.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (hasInternationalMatch && !toursData.some(
        (t) => t.category === 'Domestic' && t.title.toLowerCase().includes(searchQuery.toLowerCase())
      )) {
        navigate(`/international${paramStr}`);
      } else {
        navigate(`/domestic${paramStr}`);
      }
    }
  };

  // Featured Tours lists
  const domesticFeatured = toursData.filter((t) => t.category === 'Domestic').slice(0, 3);
  const internationalFeatured = toursData.filter((t) => t.category === 'International').slice(0, 3);

  // Countdown Deal timer state (ends in 1 day 8 hrs 24 mins)
  const [timeLeft, setTimeLeft] = useState({ days: 1, hours: 8, minutes: 24, seconds: 45 });

  useEffect(() => {
    const dealTimer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        if (prev.days > 0) return { days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        clearInterval(dealTimer);
        return prev;
      });
    }, 1000);
    return () => clearInterval(dealTimer);
  }, []);

  // Popular Destination Grid lists
  const popularDestinations = [
    { name: 'Kerala', type: 'Backwaters & Tea Hills', image: 'https://images.unsplash.com/photo-1593693397690-362cb9666fc2?auto=format&fit=crop&w=600&q=80', link: '/domestic?search=kerala' },
    { name: 'Ladakh', type: 'Mountain Odyssey', image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=600&q=80', link: '/domestic?search=ladakh' },
    { name: 'Maldives', type: 'Luxury Water Villas', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80', link: '/international?search=maldives' },
    { name: 'Swiss Alps', type: 'Scenic Villages', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=600&q=80', link: '/international?search=swiss' },
    { name: 'Bali', type: 'Heritage & Beaches', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=600&q=80', link: '/international?search=bali' },
    { name: 'Kashmir', type: 'Paradise on Earth', image: 'https://images.unsplash.com/photo-1566837945700-30057527ade0?auto=format&fit=crop&w=600&q=80', link: '/domestic?search=kashmir' }
  ];

  // Blog posts
  const blogs = [
    {
      id: 'kerala-secrets',
      title: '5 Hidden Backwater Spots in Kerala Locals Love',
      category: 'Travel Guide',
      image: 'https://images.unsplash.com/photo-1593693397690-362cb9666fc2?auto=format&fit=crop&w=600&q=80',
      date: 'June 24, 2026',
      readTime: '5 min read',
      excerpt: 'Escape the tourist hubs and find pristine waters, rural coconut groves, and absolute quiet.'
    },
    {
      id: 'ladakh-acclimatize',
      title: 'Complete Ladakh Acclimatization Checklist',
      category: 'Trekking tips',
      image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=600&q=80',
      date: 'June 20, 2026',
      readTime: '8 min read',
      excerpt: 'Essential information about altitude sickness, oxygen needs, packing layers, and key rest schedules.'
    },
    {
      id: 'bali-romantic-spots',
      title: 'Top Romantic Dinner Spots Under Balinese Skies',
      category: 'Honeymoon Guide',
      image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=600&q=80',
      date: 'June 15, 2026',
      readTime: '6 min read',
      excerpt: 'Uncover cliffside tables, jungle treehouses, and candlelight beaches made for special occasions.'
    }
  ];

  // FAQ accordion state
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: 'Can I customize the travel itinerary of my packages?',
      a: 'Absolutely! Holiday Kart specializes in tailor-made vacations. Our team of travel coordinators is happy to customize hotel levels, transportation styles, trip duration, or add/remove specific sightseeing elements.'
    },
    {
      q: 'What is your modification policy if my flight gets delayed?',
      a: 'We offer full support. If your flight is delayed or cancelled, reach out directly to your dedicated concierge coordinator. We will adjust transfer timings and hotel check-in hours to sync with your updated arrival.'
    },
    {
      q: 'Are local guide fees and entry tickets included in the price?',
      a: 'Yes. Unless specified otherwise, our pricing covers entry passes for monuments mentioned in the itinerary, boat cruise tickets, and fees for local English-speaking guides.'
    },
    {
      q: 'When do I need to pay for my vacation booking?',
      a: 'Zero payment is required when submitting an online enquiry. A travel expert will connect with you to review updates, customize preferences, and share the secure invoice. A booking deposit is collected only when you choose to confirm.'
    }
  ];

  const stats = [
    { number: '12K+', label: 'Happy Travelers' },
    { number: '150+', label: 'Stunning Spots' },
    { number: '4.9★', label: 'Average Rating' },
    { number: '24/7', label: 'Dedicated Support' }
  ];

  const features = [
    {
      icon: <Award className="feature-icon" />,
      title: 'Curated Premium Stays',
      desc: 'Hand-picked luxury boutique resorts and stays chosen for high sanitation, stunning views, and superior ratings.'
    },
    {
      icon: <ShieldCheck className="feature-icon" />,
      title: 'Gold Star Safety Measures',
      desc: 'Fully vetted professional drivers, secure routes, medical assistance protocols, and comprehensive guidance.'
    },
    {
      icon: <HeartHandshake className="feature-icon" />,
      title: 'Dedicated Holiday Concierge',
      desc: 'No automated bots. Get a dedicated real travel companion reachable via phone/WhatsApp during your entire journey.'
    }
  ];

  const testimonials = [
    {
      name: 'Anjali Sharma',
      role: 'Family Trip to Kerala',
      quote: 'Holiday Kart made our family trip to Kerala absolute heaven! The houseboat stay was spectacular, and the itinerary was perfectly balanced between activities and relaxation.',
      stars: 5,
      avatar: 'AS'
    },
    {
      name: 'Rohan Mehta',
      role: 'Adventure in Ladakh',
      quote: 'The Ladakh Odyssey was well-organized. High altitude passes are tricky, but our guide was extremely helpful, and the accommodations were surprisingly luxurious.',
      stars: 5,
      avatar: 'RM'
    },
    {
      name: 'Sophia Patel',
      role: 'Honeymoon in Maldives',
      quote: 'Booking through Holiday Kart was seamless. We got an amazing upgrade on our overwater villa and the private dining experience was worth every rupee.',
      stars: 5,
      avatar: 'SP'
    }
  ];

  return (
    <div className="home-page">
      {/* Immersive Slideshow Hero */}
      <section className="hero-section">
        <div className="hero-slides-container">
          {slides.map((slide, index) => (
            <div 
              key={index} 
              className={`hero-slide ${index === activeSlide ? 'hero-slide-active' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="hero-overlay"></div>
            </div>
          ))}
        </div>

        <div className="hero-content container">
          <div className="hero-text-area">
            <span className="hero-badge badge badge-gold">
              <Sparkles size={14} style={{ marginRight: '6px' }} />
              {slides[activeSlide].badge}
            </span>
            <h1 className="hero-title animate-title">
              Your Journey Begins with <span className="gradient-text">Holiday Kart</span>
            </h1>
            <p className="hero-subtitle">
              {slides[activeSlide].subtitle}
            </p>
            <div className="hero-ctas">
              <Link to={slides[activeSlide].link} className="btn btn-primary">
                Book This Experience <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn btn-secondary">
                Speak to Curator
              </Link>
            </div>
          </div>

          {/* Smart Tabbed Search widget */}
          <div className="search-widget glass-card">
            <div className="search-widget-header">
              <h3>Plan Your Holiday</h3>
              <p>Tailor-made itineraries at your fingertips</p>
            </div>
            
            <div className="search-category-tabs">
              <button 
                type="button"
                className={`category-tab-btn ${searchCategory === 'all' ? 'active-tab' : ''}`}
                onClick={() => setSearchCategory('all')}
              >
                All Tours
              </button>
              <button 
                type="button"
                className={`category-tab-btn ${searchCategory === 'domestic' ? 'active-tab' : ''}`}
                onClick={() => setSearchCategory('domestic')}
              >
                Domestic
              </button>
              <button 
                type="button"
                className={`category-tab-btn ${searchCategory === 'international' ? 'active-tab' : ''}`}
                onClick={() => setSearchCategory('international')}
              >
                International
              </button>
            </div>

            <form onSubmit={handleSearchSubmit} className="search-form">
              <div className="search-input-group">
                <label className="form-label">Destination</label>
                <div className="input-with-icon">
                  <Search size={18} className="input-icon" />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Kerala, Bali, Alps..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="search-grid-2">
                <div className="search-input-group">
                  <label className="form-label">Departure</label>
                  <div className="input-with-icon">
                    <Calendar size={18} className="input-icon" />
                    <input
                      type="date"
                      className="form-control"
                      value={searchDate}
                      onChange={(e) => setSearchDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="search-input-group">
                  <label className="form-label">Travelers</label>
                  <div className="input-with-icon">
                    <Users size={18} className="input-icon" />
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="20"
                      value={searchGuests}
                      onChange={(e) => setSearchGuests(Math.max(1, Number(e.target.value)))}
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-primary search-submit-btn">
                <span>Search Packages</span>
              </button>
            </form>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="hero-slide-indicators">
          {slides.map((_, index) => (
            <button 
              key={index} 
              type="button" 
              className={`slide-indicator ${index === activeSlide ? 'slide-indicator-active' : ''}`}
              onClick={() => setActiveSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      </section>

      {/* Stats Counter Section */}
      <section className="stats-section">
        <div className="stats-container container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Domestic Section */}
      <section className="section featured-domestic">
        <div className="container">
          <div className="section-header-flex">
            <div className="section-title text-left">
              <span className="section-badge">Indian Escapes</span>
              <h2>Featured Domestic Tours</h2>
              <p>Curated routes, historic sights, and cultural heritage across India.</p>
            </div>
            <Link to="/domestic" className="btn btn-outline">
              Explore All India <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-3 tours-grid">
            {domesticFeatured.map((tour) => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>
        </div>
      </section>

      {/* Flash Limited-Time Deals Section */}
      <section className="section deals-countdown-section bg-alt">
        <div className="container">
          <div className="deals-card glass-card">
            <div className="deals-badge badge badge-gold">
              <Percent size={14} style={{ marginRight: '6px' }} /> Limited Offer
            </div>
            <div className="deals-layout">
              <div className="deals-info-side">
                <h2>Maldives Luxury Escape - Save ₹15,000</h2>
                <p>Enjoy private beach dining, overwater luxury villas, and deep snorkeling experiences. Fully customizable itinerary with airport premium speed boat transfers included.</p>
                <div className="deal-pricing-row">
                  <span className="deal-price-original">₹89,999</span>
                  <span className="deal-price-current">₹74,999</span>
                  <span className="deal-price-label">/ person</span>
                </div>
                
                {/* Live Countdown Timer */}
                <div className="deal-timer-area">
                  <span className="timer-title">Deal Expires In:</span>
                  <div className="timer-digits-row">
                    <div className="timer-block">
                      <span className="timer-num">{timeLeft.days}</span>
                      <span className="timer-lbl">Day</span>
                    </div>
                    <span className="timer-divider">:</span>
                    <div className="timer-block">
                      <span className="timer-num">{timeLeft.hours.toString().padStart(2, '0')}</span>
                      <span className="timer-lbl">Hrs</span>
                    </div>
                    <span className="timer-divider">:</span>
                    <div className="timer-block">
                      <span className="timer-num">{timeLeft.minutes.toString().padStart(2, '0')}</span>
                      <span className="timer-lbl">Mins</span>
                    </div>
                    <span className="timer-divider">:</span>
                    <div className="timer-block">
                      <span className="timer-num">{timeLeft.seconds.toString().padStart(2, '0')}</span>
                      <span className="timer-lbl">Secs</span>
                    </div>
                  </div>
                </div>

                <div className="deal-actions">
                  <Link to="/tour/maldives-escape" className="btn btn-primary">
                    Claim Deal Now <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
              <div className="deals-image-side">
                <img 
                  src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&q=80" 
                  alt="Maldives Overwater Resort" 
                  className="deal-preview-img" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured International Section */}
      <section className="section featured-international">
        <div className="container">
          <div className="section-header-flex">
            <div className="section-title text-left">
              <span className="section-badge">Global Escapes</span>
              <h2>Featured International Tours</h2>
              <p>Top global destinations with pristine beaches, alpine heights, and cultural retreats.</p>
            </div>
            <Link to="/international" className="btn btn-outline">
              Explore Worldwide <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-3 tours-grid">
            {internationalFeatured.map((tour) => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destination Grid (Masonry feel) */}
      <section className="section popular-destinations-section">
        <div className="container">
          <div className="section-title text-center">
            <span className="section-badge">Top Inquiries</span>
            <h2>Trending Destinations</h2>
            <p>Popular spots highly requested by our clients this season.</p>
          </div>

          <div className="destinations-grid">
            {popularDestinations.map((dest, idx) => (
              <Link key={idx} to={dest.link} className="dest-grid-card">
                <img src={dest.image} alt={dest.name} className="dest-card-image" />
                <div className="dest-card-overlay">
                  <div className="dest-card-info">
                    <h3>{dest.name}</h3>
                    <span>{dest.type}</span>
                  </div>
                  <span className="dest-explore-btn">
                    Explore <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-choose-us-section bg-alt">
        <div className="container">
          <div className="section-title text-center">
            <span className="section-badge">Our Commitment</span>
            <h2>The Holiday Kart Standards</h2>
            <p>Why travelers trust us with their dream honeymoons and family excursions.</p>
          </div>

          <div className="grid grid-3 features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card glass-card">
                <div className="feature-icon-wrapper">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section testimonials-section">
        <div className="container">
          <div className="section-title text-center">
            <span className="section-badge">Client Stories</span>
            <h2>Loved by Our Guests</h2>
            <p>Unfiltered feedback from couples, solo explorers, and families who traveled with us.</p>
          </div>

          <div className="grid grid-3 testimonials-grid">
            {testimonials.map((t, idx) => (
              <div key={idx} className="testimonial-card glass-card">
                <div className="testimonial-stars">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <span key={i} className="star-gold">★</span>
                  ))}
                </div>
                <p className="testimonial-quote">"{t.quote}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{t.avatar}</div>
                  <div className="author-info">
                    <h4>{t.name}</h4>
                    <span>{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Inspiration Blog Section */}
      <section className="section blogs-section bg-alt">
        <div className="container">
          <div className="section-title text-center">
            <span className="section-badge">Travel Guides</span>
            <h2>Inspiration & Travel Intel</h2>
            <p>Articles crafted by our destination curators to help you prep for your journeys.</p>
          </div>

          <div className="grid grid-3 blogs-grid">
            {blogs.map((blog) => (
              <div key={blog.id} className="blog-card glass-card">
                <div className="blog-img-wrapper">
                  <img src={blog.image} alt={blog.title} className="blog-img" />
                  <span className="blog-cat-badge">{blog.category}</span>
                </div>
                <div className="blog-content">
                  <div className="blog-meta">
                    <span className="blog-meta-item"><CalendarDays size={14} /> {blog.date}</span>
                    <span className="blog-meta-item"><Clock size={14} /> {blog.readTime}</span>
                  </div>
                  <h3>{blog.title}</h3>
                  <p>{blog.excerpt}</p>
                  <Link to="/about" className="blog-link">
                    <span>Read Article</span> <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ accordion section */}
      <section className="section faq-section">
        <div className="container">
          <div className="section-title text-center">
            <span className="section-badge">Got Questions?</span>
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about planning and customizing your bookings.</p>
          </div>

          <div className="faq-accordion-list">
            {faqs.map((faq, idx) => (
              <div 
                key={idx} 
                className={`faq-item glass-card ${openFaq === idx ? 'faq-item-open' : ''}`}
              >
                <div className="faq-question" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}>
                  <h3>{faq.q}</h3>
                  {openFaq === idx ? <ChevronUp size={20} className="faq-toggle-icon" /> : <ChevronDown size={20} className="faq-toggle-icon" />}
                </div>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky floating WhatsApp trigger */}
      <a 
        href="https://wa.me/919296206555?text=Hi%20Holiday%20Kart%2C%20I'm%20planning%20a%20vacation%20and%20would%20like%20to%20customize%20a%20tour%20package." 
        className="whatsapp-float-btn" 
        target="_blank" 
        rel="noopener noreferrer"
        aria-label="Contact on WhatsApp"
      >
        <MessageCircle size={28} />
        <span className="tooltip-text">Chat with curator</span>
      </a>

      {/* Call to Action Banner */}
      <section className="cta-banner-section container">
        <div className="cta-banner-card glass-card">
          <div className="cta-banner-content">
            <h2>Ready to plan your dream vacation?</h2>
            <p>Contact our holiday curators today and get a customized travel package built just for you.</p>
          </div>
          <div className="cta-banner-actions">
            <Link to="/book" className="btn btn-primary">
              Customize My Trip
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              Speak to Curator
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
